// Derived numbers for the position tables, shared by the trade and portfolio pages.
import { bustPrice } from '../../../offchain/src/math.ts';
import { netFromCredited } from './format.ts';
import { fillPx, type Globals, type LivePosition, type Market } from './protocol.ts';
import type { ApiClosed } from './relayer.ts';

export type PositionView = {
  p: LivePosition;
  notional: bigint;
  bust?: bigint;
  mark?: bigint;
  net?: bigint;
  /** net / margin, percent */
  roe?: number;
  /** 0 = at entry, 1 = at the bust price */
  risk?: number;
};

export function viewPosition(p: LivePosition, markets: Market[] | undefined, g: Globals | undefined): PositionView {
  const m = markets?.find((x) => x.id === p.instrumentId);
  const bust = m ? bustPrice(p.entryPx, p.leverage, p.isLong, m.inst.bustBuffer) : undefined;
  const net = p.credited !== undefined && g ? netFromCredited(p.credited, p.margin, g.winFeeRate) : undefined;
  // the price the position would close at, and the one a liquidation looks at (the trader's better side)
  const exit = fillPx(m, !p.isLong);
  const liqSide = fillPx(m, p.isLong);
  let risk: number | undefined;
  if (bust !== undefined && liqSide) {
    const span = Number(p.entryPx - bust);
    risk = span === 0 ? 1 : Math.min(1, Math.max(0, Number(p.entryPx - liqSide) / span));
  }
  return {
    p,
    notional: p.margin * p.leverage,
    bust,
    mark: exit,
    net,
    roe: net !== undefined ? Number((net * 10_000n) / p.margin) / 100 : undefined,
    risk,
  };
}

/** Realized result of a closed position, net of fees */
export function closedNet(h: ApiClosed, g: Globals | undefined): bigint {
  if (h.outcome === 'liquidated') return -h.margin;
  // a win closed while the house could not pay carries no fee: it is owed net of the current rate
  if (h.credited > 0n && h.fee === 0n && g) return netFromCredited(h.credited, h.margin, g.winFeeRate);
  if (h.credited > 0n) return h.credited - h.fee;
  return h.credited < -h.margin ? -h.margin : h.credited;
}

/** HyperEVM small blocks come about once a second: good enough for "x minutes ago" from a block number */
export function blockAgeSec(head: bigint | undefined, block: bigint): number | undefined {
  return head === undefined ? undefined : Math.floor(Date.now() / 1000) - Number(head - block);
}
